import { Alert } from '@mui/material'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../..'
import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  isQuestionUnanswered,
} from '../../actions/defectAssistant'
import {
  AnswerTypes,
  DefectAssistantSuggestion,
  OptionKinds,
  OptionValue,
} from '../../actions/defectAssistant/types'
import { createDefectCommentAction } from '../../actions/defectComments/actions'
import {
  applyQuestionAnswer,
  clearCreateDefectAssistantErrors,
  excludeSuggestions,
  resetAnswersFromIndex,
  rewindToRound,
  selectAssistantIsSettled,
  selectCreateDefectAssistantState,
  selectPendingSteps,
  selectSuggestion,
  submitAdditionalDetails,
} from '../../reducers/createDefectAssistantSlice'
import { selectSuggestionRoundsVM } from '../../reducers/createDefectAssistantViewModel'
import { clearCreateStatusByDefectId } from '../../reducers/defectComments'
import ConfirmationDialog from './ConfirmationDialog'
import { AiButton, EscapeLink } from './CtaButtons'
import {
  FinishStep,
  Interlude,
  SuggestionsBlock,
  SummarySection,
} from './scroll-ai-flow/flowSections'
import { QuestionSection } from './scroll-ai-flow/QuestionSection'
import { PlaceholderSection } from './scroll-ai-flow/questionSections'
import {
  Container,
  OptionDesc,
  OptionsList,
  OptionTitle,
  SCROLL_DELAY_MS,
  ScrollOptionButton,
  Section,
  SectionReasoning,
} from './scroll-ai-flow/styles'
import { useSuggestedAmendComment } from './scroll-ai-flow/useSuggestedAmendComment'
import { useSummaryStream } from './scroll-ai-flow/useSummaryStream'
import { CreateDefectFormData } from './types'

interface ScrollAiFlowProps {
  applyAiSuggestion: (defectCode: string, defectType: string) => void
  onEnterManualNoAi: () => void
  onEnterManualAfterAi: () => void
}

const ScrollAiFlow = ({
  applyAiSuggestion,
  onEnterManualNoAi,
  onEnterManualAfterAi,
}: ScrollAiFlowProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const { setValue } = useFormContext<CreateDefectFormData>()

  const state = useTypedSelector(selectCreateDefectAssistantState)
  const { flow, body, questionAnswers, summary } = state
  const roundVMs = useTypedSelector(selectSuggestionRoundsVM)
  const isSettled = useTypedSelector(selectAssistantIsSettled)
  const pendingSteps = useTypedSelector(selectPendingSteps)

  const [additionalDetails, setAdditionalDetails] = useState('')
  const [amendDefectId, setAmendDefectId] = useState<string | null>(null)
  const [showAbortConfirm, setShowAbortConfirm] = useState(false)
  const [reEditingIdx, setReEditingIdx] = useState<number | null>(null)
  const [amendReOpenedQuestionUuid, setAmendReOpenedQuestionUuid] = useState<string | null>(null)
  const amendStatus = useTypedSelector((s) =>
    amendDefectId ? s.defectComments.createStatusByDefectId[amendDefectId] : undefined
  )

  const activeRef = useRef<HTMLDivElement>(null)
  const questionFlowThunkRef = useRef<{ abort: () => void } | null>(null)

  const dispatchAdvanceQuestionFlow = useCallback(
    (arg?: { blockingSummary: boolean }) => {
      questionFlowThunkRef.current?.abort()
      questionFlowThunkRef.current = dispatch(advanceQuestionFlowAction(arg))
    },
    [dispatch]
  )

  const abandonQuestionTurn = () => {
    questionFlowThunkRef.current?.abort()
    questionFlowThunkRef.current = null
  }

  const reOpenQuestion = (qnaIdx: number) => {
    abandonQuestionTurn()
    setAmendReOpenedQuestionUuid(null)
    dispatch(resetAnswersFromIndex(qnaIdx))
  }

  const reEditQuestion = (qnaIdx: number) => {
    abandonQuestionTurn()
    setAmendReOpenedQuestionUuid(null)
    setReEditingIdx(qnaIdx)
  }

  const firstUnansweredIdx = questionAnswers.findIndex(isQuestionUnanswered)
  const currentQuestion = firstUnansweredIdx >= 0 ? questionAnswers[firstUnansweredIdx] : undefined
  const lastAnsweredIdx = questionAnswers.reduce(
    (acc, q, idx) => (isQuestionUnanswered(q) ? acc : idx),
    -1
  )

  const questionLoading = flow.step === 'questioning' && flow.loading
  const flowComplete = isSettled && !questionLoading && reEditingIdx === null
  const showSummary = summary.status !== 'idle' && reEditingIdx === null

  useEffect(() => {
    if (reEditingIdx === null) return
    const q = questionAnswers[reEditingIdx]
    if (!q || q.answerType !== AnswerTypes.MULTISELECT) setReEditingIdx(null)
  }, [questionAnswers, reEditingIdx])

  useSummaryStream(dispatchAdvanceQuestionFlow)
  const suggestedAmendComment = useSuggestedAmendComment()

  useEffect(() => {
    if (!activeRef.current) return
    const timer = setTimeout(() => {
      activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [firstUnansweredIdx, flowComplete])

  const handlePickSuggestion = (suggestion: DefectAssistantSuggestion, roundIdx: number) => {
    applyAiSuggestion(suggestion.defectCode, '')
    dispatch(selectSuggestion({ suggestion, roundIdx }))
    dispatchAdvanceQuestionFlow()
  }

  const handleExcludeSuggestions = (roundIdx: number) => {
    abandonQuestionTurn()
    dispatch(excludeSuggestions(roundIdx))
  }

  const handleRewindToRound = (roundIdx: number) => {
    abandonQuestionTurn()
    dispatch(rewindToRound(roundIdx))
  }

  const handleExcludeOrRewind = (roundIdx: number) => {
    if (roundIdx < roundVMs.length - 1) {
      handleRewindToRound(roundIdx)
      return
    }
    handleExcludeSuggestions(roundIdx)
  }

  const enterManualNoAi = () => {
    if (body.defectCode) applyAiSuggestion(body.defectCode, body.defectType)
    onEnterManualNoAi()
  }

  const handleMetaOption = (option: OptionValue, roundIdx: number) => {
    if (option.kind === OptionKinds.GIVE_UP) {
      setValue('comment', body.userComment)
      enterManualNoAi()
      return
    }
    handleExcludeOrRewind(roundIdx)
  }

  const handleSubmitAdditionalDetails = () => {
    const trimmed = additionalDetails.trim()
    if (trimmed.length > 0) {
      const nextComment =
        body.userComment.length > 0 ? `${body.userComment}\n\n${trimmed}` : trimmed
      setValue('comment', nextComment)
    }
    dispatch(submitAdditionalDetails(trimmed))
    setAdditionalDetails('')
    dispatch(analyzeSuggestionsAction())
  }

  const submitAnswers = (answers: OptionValue[]) => {
    if (questionLoading) return
    dispatch(applyQuestionAnswer(answers))
    dispatchAdvanceQuestionFlow()
  }

  const handleFinishAndSend = () => {
    const amendAnswer = questionAnswers
      .flatMap((qna) => qna.answers ?? [])
      .find((answer) => answer.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT)
    if (amendAnswer) {
      const defectId = amendAnswer.value != null ? String(amendAnswer.value) : ''
      const comment = amendAnswer.additionalInfo || suggestedAmendComment.comment || ''
      if (defectId) {
        setAmendDefectId(defectId)
        dispatch(createDefectCommentAction({ defectId, comment }))
        return
      }
    }

    setValue('aiComment', summary.text)
    setValue('comment', body.userComment)

    if (body.restrictionCode?.length) setValue('restrictionCode', body.restrictionCode)
    setValue('positionInVehicle', body.vehiclePosition || '')

    applyAiSuggestion(body.defectCode, body.defectType)
    onEnterManualAfterAi()
  }

  if (flow.step === 'failed') {
    return (
      <Container>
        <Alert severity="error">
          {flow.source === 'analyze'
            ? t('defect.aiAssistant.error.suggestions')
            : t('defect.aiAssistant.error.questions')}
        </Alert>
        <AiButton
          type="button"
          onClick={() => {
            dispatch(clearCreateDefectAssistantErrors())
            dispatch(analyzeSuggestionsAction())
          }}
        >
          {t('defect.aiAssistant.retry')}
        </AiButton>
      </Container>
    )
  }

  const visiblePendingSteps = flow.step !== 'idle' && reEditingIdx === null ? pendingSteps : []

  return (
    <Container>
      {roundVMs.map((round) => {
        const interlude = round.interlude ? (
          <Interlude
            mode={round.interlude.mode}
            detail={round.interlude.detail}
            value={additionalDetails}
            onChange={setAdditionalDetails}
            onSubmit={handleSubmitAdditionalDetails}
            isScrollAnchor={round.isScrollAnchor}
          />
        ) : null

        return (
          <Fragment key={round.key}>
            {round.empty ? (
              <Section>
                <SectionReasoning>{t('defect.aiAssistant.noSuggestions')}</SectionReasoning>
              </Section>
            ) : (
              <SuggestionsBlock
                round={round}
                onPick={(suggestion) => handlePickSuggestion(suggestion, round.roundIdx)}
                onMeta={(option) => handleMetaOption(option, round.roundIdx)}
                onExclude={() => handleExcludeOrRewind(round.roundIdx)}
              />
            )}
            {interlude}
            {round.showRetryEscape && (
              <Section>
                <OptionsList>
                  <ScrollOptionButton type="button" optionState="active" onClick={enterManualNoAi}>
                    <OptionTitle>{t('defect.aiAssistant.noneOfTheseTitle')}</OptionTitle>
                    <OptionDesc>{t('defect.aiAssistant.noneOfTheseManualReasoning')}</OptionDesc>
                  </ScrollOptionButton>
                </OptionsList>
              </Section>
            )}
          </Fragment>
        )
      })}

      {questionAnswers.map((qna, idx) => (
        <QuestionSection
          key={qna.uuid}
          qna={qna}
          qnaIdx={idx}
          reEditingIdx={reEditingIdx}
          currentQuestion={currentQuestion}
          lastAnsweredIdx={lastAnsweredIdx}
          questionLoading={questionLoading}
          amendLoading={amendStatus === 'loading'}
          showCommentFieldInitially={amendReOpenedQuestionUuid === qna.uuid}
          suggestedComment={suggestedAmendComment.comment}
          suggestedCommentLoading={suggestedAmendComment.loading}
          activeRef={activeRef}
          onSubmit={submitAnswers}
          onReEditSubmit={(answers) => {
            dispatch(resetAnswersFromIndex(idx))
            dispatch(applyQuestionAnswer(answers))
            dispatchAdvanceQuestionFlow()
            setReEditingIdx(null)
          }}
          onReOpen={() => reOpenQuestion(idx)}
          onReEdit={() => reEditQuestion(idx)}
          onReOpenForAmend={() => setAmendReOpenedQuestionUuid(qna.uuid)}
          onAmendComment={(defectId, comment) => {
            setAmendDefectId(defectId)
            dispatch(createDefectCommentAction({ defectId, comment }))
          }}
          onAbortRequest={() => setShowAbortConfirm(true)}
        />
      ))}

      {visiblePendingSteps.map((fq, idx) => (
        <PlaceholderSection
          key={fq.type}
          label={fq.label}
          firstLoading={questionLoading && !currentQuestion && idx === 0}
        />
      ))}

      {showSummary && (
        <SummarySection
          text={summary.text}
          streaming={summary.status === 'streaming'}
          failed={summary.status === 'failed'}
        />
      )}

      <FinishStep
        flowComplete={flowComplete}
        amendLoading={amendStatus === 'loading'}
        activeRef={activeRef}
        onFinish={handleFinishAndSend}
      />

      <SectionReasoning style={{ textAlign: 'center', padding: '8px 4px' }}>
        {t('defect.aiAssistant.editSelectionsHint')}
      </SectionReasoning>

      {!flowComplete && (
        <EscapeLink type="button" onClick={enterManualNoAi}>
          {t('defect.aiAssistant.continueToForm')}
        </EscapeLink>
      )}

      {amendStatus === 'failed' && (
        <Alert severity="error">{t('defect.comment.create.error')}</Alert>
      )}
      {amendStatus === 'succeeded' && amendDefectId && (
        <ConfirmationDialog
          dialogText={t('defect.comment.create.success')}
          primaryActionText={t('back')}
          closeDialog={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
          }}
          submitHandler={() => {
            dispatch(clearCreateStatusByDefectId(amendDefectId))
            setAmendDefectId(null)
            window.history.back()
          }}
        />
      )}
      {showAbortConfirm && (
        <ConfirmationDialog
          dialogText={t('defect.aiAssistant.duplicate.abortConfirmText')}
          primaryActionText={t('defect.aiAssistant.duplicate.abortConfirmButton')}
          secondaryActionText={t('defect.aiAssistant.duplicate.abortCancelButton')}
          closeDialog={() => setShowAbortConfirm(false)}
          submitHandler={() => window.history.back()}
        />
      )}
    </Container>
  )
}

export default ScrollAiFlow
